/**
 * Tag color helpers.
 *
 * Tag pills keep a solid background (the tag color); the text is white on
 * dark colors, black on light colors. Contrast is decided by PERCEIVED
 * brightness (YIQ), so saturated reds/blues/greens get white text and pale
 * colors get black text.
 */

/** Perceived brightness (0-255), YIQ formula. Low = dark = needs white text. */
function hexBrightness(hex: string): number {
  let color = (hex || "").replace(/^#/, "");
  if (color.length === 3) {
    color = color.split("").map((c) => c + c).join("");
  }
  if (!/^[0-9a-fA-F]{6}$/.test(color)) return 128;

  const r = parseInt(color.slice(0, 2), 16);
  const g = parseInt(color.slice(2, 4), 16);
  const b = parseInt(color.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000;
}

/** True when the color is dark enough to need white text (perceived brightness < 170). */
export function isDarkColor(hex: string): boolean {
  return hexBrightness(hex) < 170;
}

/** White text on dark backgrounds, black text on light backgrounds. */
export function getContrastTextColor(backgroundColor: string): string {
  return isDarkColor(backgroundColor) ? "#FFFFFF" : "#000000";
}

/** Mixes a hex color toward white. ratio = share of the original color kept (0 = white, 1 = color). */
function mixWithWhite(hex: string, ratio: number): string {
  let color = (hex || "").replace(/^#/, "");
  if (color.length === 3) {
    color = color.split("").map((c) => c + c).join("");
  }
  if (!/^[0-9a-fA-F]{6}$/.test(color)) return hex;

  const mix = (channel: number) => Math.round(255 - (255 - channel) * ratio);
  const r = mix(parseInt(color.slice(0, 2), 16));
  const g = mix(parseInt(color.slice(2, 4), 16));
  const b = mix(parseInt(color.slice(4, 6), 16));
  const toHex = (n: number) => n.toString(16).padStart(2, "0").toUpperCase();
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Derives the full reception palette (background tint, border tint, bar) from a
 * single base color, so receptions can use any color like tags do.
 */
export function deriveReceptionPalette(color: string): {
  color: string;
  bgColor: string;
  borderColor: string;
  barColor: string;
} {
  return {
    color,
    bgColor: mixWithWhite(color, 0.13),
    borderColor: mixWithWhite(color, 0.24),
    barColor: color,
  };
}

/** 40-color palette offered when picking a tag color. */
export const TAG_COLOR_PALETTE: string[] = [
  "#EF4444", "#F87171", "#FCA5A5", "#F97316", "#FB923C",
  "#FDBA74", "#F59E0B", "#FBBF24", "#FCD34D", "#EAB308",
  "#FACC15", "#FDE047", "#84CC16", "#A3E635", "#22C55E",
  "#4ADE80", "#10B981", "#34D399", "#14B8A6", "#2DD4BF",
  "#06B6D4", "#22D3EE", "#0EA5E9", "#38BDF8", "#7DD3FC",
  "#3B82F6", "#60A5FA", "#93C5FD", "#6366F1", "#818CF8",
  "#8B5CF6", "#A78BFA", "#A855F7", "#C084FC", "#D946EF",
  "#EC4899", "#F472B6", "#F43F5E", "#6B7280", "#94A3B8",
];
